import { captureException } from "@sentry/react";

/**
 * Orchestrates a single chat-send round-trip:
 *  1. Reads the current active slot's sessionId + conversationId, captures
 *     them as `sentSessionId` / `sentConversationId` for the rest of the
 *     dispatch. This is CRITICAL: every state mutation in the .then/.catch
 *     uses the CAPTURED sessionId, not whatever `activeId` happens to be
 *     when the promise resolves. Without this, switching conversations
 *     mid-flight would clobber the wrong slot.
 *  2. Transitions the slot to "generating" (if it already has a convId) or
 *     "pending" (new chat) so the UI immediately reflects the in-flight
 *     state — closes the double-send race.
 *  3. Fires sendChat. The server answers as soon as the turn has started
 *     (the answer itself only arrives on the subscription), so success just
 *     attaches the convId; the stream's `stop` marks the slot done. On error,
 *     captures the exception to Sentry, appends an error message to the
 *     ORIGINAL slot's chat history (not the now-active one — so the error
 *     ends up in the conversation the user sent from), and marks the slot
 *     done so the spinner clears and the user can retry.
 *  4. Appends the user message to the ACTIVE slot's chat history at
 *     dispatch time. The user typed in the active slot, so that's where
 *     it visually belongs.
 *  5. Watches for the turn to start. The server publishes the stream's
 *     `start` event before any DB or model work, so a slot with no stream
 *     event after START_TIMEOUT_MS never reached the agent. Without this
 *     check the request could hang with no timeout and "Working…" stayed up
 *     forever, holding any queued message behind it (issue 5552, 2026-10-23).
 *  6. Once the response is in, or its transport failed after the stream
 *     started (a 5xx, a timeout, a dropped connection: the turn runs on the
 *     server regardless), completion is left to the stream's `stop` event,
 *     until the stream goes quiet for a few heartbeats. A quiet stream is
 *     reloaded from the saved conversation first (`recoverTurn`), since the
 *     turn may have finished with its `stop` lost; only a turn that was not
 *     saved fails. A GraphQL error still fails the turn (it may come with no
 *     `stop`), as does any failure before the stream started.
 *
 * Extracted from RangerChat for isolated testing. The component supplies
 * the store accessors and Apollo's `sendChat` trigger; this function
 * doesn't know about React or zustand directly.
 */
/** No stream event this long after sending means the turn never started. The
 * server publishes `start` before any work, so a few seconds is normal. */
export const START_TIMEOUT_MS = 45_000;
/** A running turn's stream gets this many heartbeat intervals of silence
 * before it is given up. The server pings a live turn's stream, even while a
 * long tool runs, and each ping says how often (CHAT_HEARTBEAT_MS in
 * LLMChat.provider), so silence this long means the turn's pod died or the
 * socket lost the rest of the stream. */
const MISSED_HEARTBEATS = 4;
/** The interval assumed until a turn's first heartbeat says otherwise. */
const ASSUMED_HEARTBEAT_MS = 30_000;
export const STREAM_IDLE_TIMEOUT_MS = MISSED_HEARTBEATS * ASSUMED_HEARTBEAT_MS;
/** How often to check a running turn's stream. */
const STREAM_IDLE_CHECK_MS = 15_000;
/** How long reloading the saved conversation may take before the turn is
 * failed instead: nothing else is watching the turn by then. */
const RECOVER_TIMEOUT_MS = 20_000;

export interface DispatchMessageDeps {
  /** Snapshot of just the fields we need from the store at dispatch time. */
  state: {
    activeId: string;
    slots: ReadonlyMap<string, { conversationId: number | null }>;
  };
  sendChat: (vars: { variables: ChatRequestVariables }) => Promise<{
    data?: { chat?: { conversationId?: number | null } };
    // Apollo's lazy-query execute resolves (does not reject) on a GraphQL
    // error, carrying it here. Read it — don't rely on `.catch`. An
    // ApolloError has graphQLErrors when the server answered with errors;
    // without them it was the transport (timeout, 5xx, dropped socket).
    error?: { message?: string; graphQLErrors?: readonly unknown[]; networkError?: { statusCode?: number } | null };
  }>;
  /** Live read of a slot, for the timers that fire after dispatch. */
  getSlot: (
    sessionId: string
  ) =>
    | {
        status: "pending" | "generating" | "done";
        streamStarted?: boolean;
        lastStreamEventAt?: number;
        heartbeatMs?: number;
        messageId?: string;
      }
    | undefined;
  /** Fill the slot from its saved conversation; true when it holds the turn
   * that asked `question`. */
  recoverTurn: (sessionId: string, question: string) => Promise<boolean>;
  actions: {
    patchSlot: (
      sessionId: string,
      patch: {
        conversationId?: number | null;
        status?: "pending" | "generating" | "done";
        streamStarted?: boolean;
        messageId?: string;
      }
    ) => void;
    setSlotStatus: (sessionId: string, status: "pending" | "generating" | "done") => void;
    /** Append a message to the ACTIVE slot's chat history (used for the
     * outgoing user message at dispatch time). */
    appendToActiveChatHistory: (message: { type: "user"; id: string; data: { type: "markdown"; message: string } }) => void;
    /** Append a message to a SPECIFIC slot's chat history (the error message
     * in .catch), where we need to target the sent slot, not whichever slot
     * the user is now looking at). */
    appendToSlotChatHistory: (
      sessionId: string,
      message: { type: "agent"; data: { type: "error"; message: string } }
    ) => void;
  };
}

export interface ChatRequestVariables {
  question: string;
  conversationId: number | null;
  chatContext: unknown;
  tools: string[];
  sessionId: string;
  model: string;
}

export interface DispatchMessageArgs {
  messageText: string;
  /** Builder for the rest of the GraphQL variables — gets the resolved
   * sessionId + conversationId from this function and returns the full
   * variables object the lazy query expects. */
  buildVariables: (params: {
    sessionId: string;
    conversationId: number | null;
    messageText: string;
  }) => ChatRequestVariables;
}

export interface DispatchMessageResult {
  /** The sessionId captured at dispatch time. Tests can use this to verify
   * the closure-captured value is correct independent of subsequent
   * activeId changes. */
  sentSessionId: string;
  /** Resolves when the sendChat promise settles AND the corresponding slot
   * lifecycle transition has been applied. Useful for tests; the component
   * itself doesn't need to await it because state updates drive re-render. */
  promise: Promise<void>;
}

export function dispatchMessage(
  deps: DispatchMessageDeps,
  args: DispatchMessageArgs
): DispatchMessageResult {
  const { state, sendChat, actions, getSlot, recoverTurn } = deps;
  const sentSessionId = state.activeId;
  const sentSlot = state.slots.get(sentSessionId);
  const sentConversationId = sentSlot?.conversationId ?? null;
  const userMessageId = `user-${crypto.randomUUID()}`;
  // A watchdog belongs to this message: once another is sent from the slot, a
  // turn that finished quickly must not judge the next message's turn.
  const superseded = () => getSlot(sentSessionId)?.messageId !== userMessageId;

  if (sentConversationId !== null) {
    actions.patchSlot(sentSessionId, { status: "generating", streamStarted: false, messageId: userMessageId });
  } else {
    actions.patchSlot(sentSessionId, { streamStarted: false, messageId: userMessageId });
    actions.setSlotStatus(sentSessionId, "pending");
  }

  const fail = (message: string) => {
    // Already finished (the stream's `stop`, a stop click, or the start
    // watchdog), or the slot has moved on to another message: a late failure
    // must not add a second bubble, or end the next message's turn.
    if (superseded() || getSlot(sentSessionId)?.status === "done") return;
    actions.appendToSlotChatHistory(sentSessionId, {
      type: "agent",
      data: { type: "error", message },
    });
    actions.setSlotStatus(sentSessionId, "done");
  };
  const streamRunning = () => {
    const slot = getSlot(sentSessionId);
    return slot !== undefined && slot.status !== "done" && slot.streamStarted === true;
  };
  // The turn is running and only the stream can finish it: let `stop` do so,
  // unless the stream goes quiet (the pod died, or the socket dropped `stop`),
  // in which case the saved conversation shows whatever the server finished.
  const awaitStreamEnd = () => {
    let lastTick = Date.now();
    const idleCheck = setInterval(() => {
      const slot = getSlot(sentSessionId);
      if (!slot || slot.status === "done" || superseded()) {
        clearInterval(idleCheck);
        return;
      }
      // A tick long after the last one means the tab was frozen or the machine
      // slept, and the socket is still catching up: judge on the next tick.
      const now = Date.now();
      const resumed = now - lastTick > 2 * STREAM_IDLE_CHECK_MS;
      lastTick = now;
      if (resumed) return;
      // A stream that never started is the start watchdog's to end.
      if (!slot.streamStarted) return;
      const idleLimit = MISSED_HEARTBEATS * (slot.heartbeatMs ?? ASSUMED_HEARTBEAT_MS);
      if (now - (slot.lastStreamEventAt ?? 0) >= idleLimit) {
        clearInterval(idleCheck);
        captureException(new Error("chat stream went silent before its stop event"), {
          tags: { feature: "rangerChat:streamLost" },
        });
        const lost = "Lost the connection to this answer. Reload the page to see it if it finished.";
        const timedOut = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("reloading the conversation timed out")), RECOVER_TIMEOUT_MS)
        );
        Promise.race([recoverTurn(sentSessionId, args.messageText), timedOut])
          .then(recovered => {
            if (superseded()) return;
            if (!recovered) return fail(lost);
            // A `stop` that arrived meanwhile already finished the slot.
            if (getSlot(sentSessionId)?.status !== "done") actions.setSlotStatus(sentSessionId, "done");
          })
          .catch((err: unknown) => {
            captureException(err, { tags: { feature: "rangerChat:recoverTurn" } });
            fail(lost);
          });
      }
    }, STREAM_IDLE_CHECK_MS);
  };

  // The response failed in transport, but the stream shows the turn running on
  // the server: the stream decides how it ends.
  const responseLost = (err: unknown) => {
    captureException(err, { tags: { feature: "rangerChat:responseLost" }, level: "warning" });
    awaitStreamEnd();
  };

  // Not cleared when the response arrives: the response comes back as soon as
  // the turn starts, and a turn whose stream never reaches this tab must still
  // be ended. A no-op once the stream has started.
  setTimeout(() => {
    if (superseded()) return;
    const slot = getSlot(sentSessionId);
    if (slot && slot.status !== "done" && !slot.streamStarted) {
      captureException(new Error("chat turn did not start"), { tags: { feature: "rangerChat:noStart" } });
      fail("Sirius hasn't started on your message, so the connection may have dropped. Please send it again.");
    }
  }, START_TIMEOUT_MS);

  const promise = sendChat({
    variables: args.buildVariables({
      sessionId: sentSessionId,
      conversationId: sentConversationId,
      messageText: args.messageText,
    }),
  })
    .then(({ data, error }) => {
      const chat = data?.chat;
      // Apollo's lazy-query execute RESOLVES (never rejects) on a GraphQL
      // error — the error rides on the result and `data` is undefined. The old
      // handler read only `data` and left the real error handling to `.catch`,
      // which therefore never ran: the slot went straight to "done" and the
      // failure rendered nothing (silent spinner-clear). Treat a populated
      // `error` — or a missing chat payload — as the failure path here.
      if (error || !chat) {
        if (error && !error.graphQLErrors?.length && streamRunning()) return responseLost(error);
        captureException(error ?? new Error("chat query returned no data"), {
          tags: { feature: "rangerChat:dispatchMessage" },
        });
        fail(
          error?.message
            ? `Error answering your question, please try again. ${error.message}`
            : "Error answering your question, please try again."
        );
        return;
      }
      // Targeting `sentSessionId` (captured at dispatch) is what makes the
      // mid-flight switch case safe — see the docstring above.
      const newConvId = chat.conversationId ?? null;
      if (newConvId !== null) {
        actions.patchSlot(sentSessionId, { conversationId: newConvId });
      }
      awaitStreamEnd();
    })
    .catch((err: unknown) => {
      // Genuine transport rejection (connection dropped before any response).
      // GraphQL errors take the resolved-`error` path above, not this one.
      if (streamRunning()) return responseLost(err);
      captureException(err, { tags: { feature: "rangerChat:dispatchMessage" } });
      fail(
        err instanceof Error && err.message
          ? `Error answering your question, please try again. ${err.message}`
          : "Error answering your question, please try again."
      );
    });

  actions.appendToActiveChatHistory({
    type: "user",
    id: userMessageId,
    data: { type: "markdown", message: args.messageText },
  });

  return { sentSessionId, promise };
}

/**
 * Once a turn ends, release the message queued behind it: send it on the same
 * conversation, or, in a new chat whose first turn failed (so there is no
 * conversation to continue), `restore` it to the input instead of leaving it
 * stuck unsent (issue 5552).
 */
export function releaseQueuedMessage({
  isWaitingForResponse,
  conversationId,
  consumeQueuedMessage,
  send,
  restore,
}: {
  isWaitingForResponse: boolean;
  conversationId: number | null;
  consumeQueuedMessage: () => string | null | undefined;
  send: (message: string) => void;
  restore: (message: string) => void;
}): void {
  if (isWaitingForResponse) return;
  const message = consumeQueuedMessage();
  if (!message) return;
  if (conversationId !== null) send(message);
  else restore(message);
}

/** The history without the bubbles shown for a queued message (one per line
 * queued), once that message goes back to the input. */
export function withoutQueuedMessage<M extends { type: string; data: unknown }>(history: M[], queued: string): M[] {
  const toRemove = new Set(queued.split("\n"));
  return history.filter(m => {
    if (m.type !== "user") return true;
    const text = (m.data as { message?: unknown }).message;
    if (typeof text !== "string" || !toRemove.has(text)) return true;
    toRemove.delete(text);
    return false;
  });
}
